import React, { useEffect, useMemo } from "react";
import { z } from "zod";
import { InformationCircleIcon } from "@heroicons/react/24/solid";
import {
    useFormContext,
    Controller,
    useWatch,
    FieldErrors,
    FieldError,
} from "react-hook-form";
import { QuestionContentBlockRankingComponent } from "./QuestionContentBlockRankingComponent";
import { QuestionContentBlockErrorDisplay } from "./QuestionContentBlockErrorDisplay";
import {
    FormLabel,
    FormInput,
    FormSelect,
    FormOption,
    FormTextArea,
    FormInputRadioCheckbox,
    FormInputRange,
    FormInputToggle,
    FormToggleLabel,
    FormToggleDiv,
    FormToggleLeftLabel,
    FormToggleRightLabel,
    FormRadioOrCheckboxGroup,
} from "./QuestionComponents";
import { QuestionContentBlockFormFieldTheme } from "./types";
import {
    ColorScheme,
    ContentBlockShape,
    OptionShape,
    Prompt,
    PromptType,
    SelectionShape,
} from "@/models";
import { QuestionContentBlockDragDropComponent } from "./drag-and-drop/QuestionContentBlockDragDropComponent";
import { QuestionContentBlockTimeline } from "./drag-and-drop/QuestionContentBlockTimeline";
import {
    useGetContentBlockWithChildren,
    useContentBlockState,
} from "@/Pages/DesignStudio/Design/useContentBlockState";
import RichTextBlock from "@/Pages/DesignStudio/Display/rich-text/RichTextBlock";
import { cn } from "@/util";
import { themeToStyleVariables } from "@/tailwind-colors/themePropertyToStyleVariable";
import { useSaveContentBlock } from "@/Pages/DesignStudio/Design/content-block-management/useSaveContentBlock";
import { PanelTop, PanelBottom, PanelLeft, PanelRight } from "lucide-react";

const getClasses = (contentBlock: ContentBlockShape, ignoreList = []) => {
    return contentBlock?.theme?.tailwindClasses?.length &&
        contentBlock?.theme?.variant !== "default"
        ? cn([
              ...(contentBlock.theme?.tailwindClasses || [])?.filter(
                  (className) => !ignoreList.includes(className),
              ),
              "relative",
              "group/OPTION",
          ])
        : "flex items-center justify-between py-2 group/OPTION";
};

type QuestionContentBlockFormFieldProps = {
    contentBlockId?: string;
    promptId: string;
    prompt: Prompt;
    palette?: ColorScheme;
    theme?: QuestionContentBlockFormFieldTheme;
    optionRenderMap: { [index: string]: JSX.Element | JSX.Element[] };
    isInDesignContext: boolean;
};

const defaultCardContainerClasses = "flex flex-row flex-wrap gap-2 w-full";
const defaultCardLabelClasses =
    "flex flex-col items-center gap-2 rounded-2xl p-0 shadow-[2px_4px_12px_#00000014] cursor-pointer border-2 border-transparent self-stretch basis-full w-full";

const directionClasses = ["flex-col", "flex-col-reverse", "flex-row", "flex-row-reverse", "items-center", "items-start"];

function CardOptionLayoutControls({ contentBlockId }: { contentBlockId: string }) {
    const optionBlock = useGetContentBlockWithChildren(contentBlockId);
    const { mutateAsync } = useSaveContentBlock();
    const { setContentBlock } = useContentBlockState();

    if (!optionBlock) return null;

    const tailwindClasses = optionBlock?.theme?.tailwindClasses || [];
    const currentDirection = tailwindClasses.includes("flex-row-reverse")
        ? "row-reverse"
        : tailwindClasses.includes("flex-row")
            ? "row"
            : tailwindClasses.includes("flex-col-reverse")
                ? "col-reverse"
                : "col";

    const setDirection = async (e: React.MouseEvent, direction: "col" | "col-reverse" | "row" | "row-reverse") => {
        e.preventDefault();
        e.stopPropagation();
        const filtered = tailwindClasses.filter((cls) => !directionClasses.includes(cls));
        const newClasses = direction === "col" || direction === "col-reverse"
            ? [...filtered, `flex-${direction}`, "items-center"]
            : [...filtered, `flex-${direction}`, "items-start"];
        const updated = {
            ...optionBlock,
            theme: { ...optionBlock.theme, tailwindClasses: newClasses },
        };
        const { savedContentBlock } = await mutateAsync(updated);
        setContentBlock(savedContentBlock);
    };

    return (
        <div
            className="absolute -top-9 left-0 z-[10000] flex items-center gap-1 rounded-md border
                border-gray-200 bg-white px-1.5 py-1 shadow-md
                opacity-0 pointer-events-none transition-opacity duration-150
                group-hover/OPTION:opacity-100 group-hover/OPTION:pointer-events-auto"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
            <span className="mr-1 text-xs text-gray-400">Image:</span>
            <button
                onClick={(e) => setDirection(e, "col")}
                data-active={currentDirection === "col"}
                title="Image top"
                className="rounded p-0.5 text-gray-500 hover:bg-gray-100
                    data-[active=true]:bg-blue-50 data-[active=true]:text-blue-600"
            >
                <PanelTop className="h-4 w-4" />
            </button>
            <button
                onClick={(e) => setDirection(e, "col-reverse")}
                data-active={currentDirection === "col-reverse"}
                title="Image bottom"
                className="rounded p-0.5 text-gray-500 hover:bg-gray-100
                    data-[active=true]:bg-blue-50 data-[active=true]:text-blue-600"
            >
                <PanelBottom className="h-4 w-4" />
            </button>
            <button
                onClick={(e) => setDirection(e, "row")}
                data-active={currentDirection === "row"}
                title="Image left"
                className="rounded p-0.5 text-gray-500 hover:bg-gray-100
                    data-[active=true]:bg-blue-50 data-[active=true]:text-blue-600"
            >
                <PanelLeft className="h-4 w-4" />
            </button>
            <button
                onClick={(e) => setDirection(e, "row-reverse")}
                data-active={currentDirection === "row-reverse"}
                title="Image right"
                className="rounded p-0.5 text-gray-500 hover:bg-gray-100
                    data-[active=true]:bg-blue-50 data-[active=true]:text-blue-600"
            >
                <PanelRight className="h-4 w-4" />
            </button>
        </div>
    );
}

function CheckboxOrRadioGroup({
    promptBlock,
    children,
    theme,
    palette,
    isInDesignContext,
}: QuestionContentBlockFormFieldProps & {
    promptBlock: ContentBlockShape;
    children: React.ReactNode;
}) {
    if (promptBlock?.theme?.variant === "cards") {
        const promptClasses = promptBlock?.theme?.tailwindClasses?.length
            ? getClasses(promptBlock)
            : cn([defaultCardContainerClasses, "relative", "group/OPTION"]);
        return <div className={promptClasses}>{children}</div>;
    }

    return (
        <FormRadioOrCheckboxGroup
            spaced={theme?.spaced || "not spaced"}
            palette={palette}
            bordered={theme?.bordered || "not bordered"}
            collapsed={theme?.collapsed}
            isInDesignContext={isInDesignContext}
            className="container relative"
            hasMaxWidth={!theme?.tailwindClasses?.length}
        >
            {children}
        </FormRadioOrCheckboxGroup>
    );
}

function CardOptionImage({ optionBlock }: { optionBlock?: ContentBlockShape }) {
    if (!optionBlock?.image_url) {
        return null;
    }
    const cls = optionBlock.theme?.tailwindClasses || [];
    const imageCornerClass =
        cls.includes("flex-row") ? "rounded-l-2xl" :
        cls.includes("flex-row-reverse") ? "rounded-r-2xl" :
        cls.includes("flex-col-reverse") ? "rounded-b-2xl" :
        "rounded-t-2xl";
    return (
        <span
            className={cn(["group/image-editor relative w-full overflow-hidden", imageCornerClass])}
            data-image-content-block-id={optionBlock.id}
        >
            <img
                src={optionBlock.image_url}
                alt="optionBlock.theme.image_url"
                className="w-full object-cover"
            />
        </span>
    );
}

function FormLabelWithContentBlock({
    children,
    contentBlockId,
    option,
    watchedValue,
    theme,
    palette,
    parentId,
    type,
    promptId,
    isCards,
    isInDesignContext,
}: {
    contentBlockId: string;
    children: React.ReactNode;
    option: OptionShape;
    watchedValue: string;
    palette?: ColorScheme;
    theme?: QuestionContentBlockFormFieldTheme;
    parentId: string;
    type: "checkbox" | "radio";
    promptId: string;
    isCards?: boolean;
    isInDesignContext?: boolean;
}) {
    const optionBlock = useGetContentBlockWithChildren(contentBlockId);
    const effectiveIsCards = isCards || optionBlock?.theme?.variant === "cards";

    if (effectiveIsCards) {
        // Card-option short-circuit: when a card-variant OPTION carries
        // tailwindClasses, the option's CSS owns appearance (border, padding,
        // hover, selection state via :has(:checked)). This serves BOTH imported
        // designs (scoped source CSS) AND creator-built card options edited via
        // CardOptionLayoutControls — so the test stays on tailwindClasses, NOT
        // imported_design_id. Render a plain <label> with just the source
        // classes — no hard-coded border-6, no inline borderWidth, no
        // opinionated motion, no inner padding wrapper. Children include the
        // (hidden) radio and the rendered RichTextBlock; both stay so form
        // logic works.
        if (optionBlock?.theme?.tailwindClasses?.length) {
            // Participants get a real <label> (whole card clickable to select
            // the option). In the editor the card wraps an editable Tiptap
            // block, and a <label> sabotages text editing: a mousedown/drag
            // inside a label is special-cased by the browser to activate/focus
            // the label's control, so the click never focuses the
            // contenteditable (activeElement stays on <body>) and you can't
            // drag-select — which means the BubbleMenu never shows. Render a
            // <div> in design context so the editor behaves like a normal
            // editable region. z-[1000] keeps the card above the question-level
            // select overlay so clicks land here.
            const CardTag = (isInDesignContext ? "div" : "label") as
                | "div"
                | "label";
            // Creator-built cards style selection with
            // `data-[selected=true]:…` classes and fill with
            // `bg-[var(--bg-color)]`, so the card needs data-selected and
            // its theme colour variables. Imported cards skip the variables:
            // their scoped source CSS owns colour.
            const isImported = !!optionBlock.theme.imported_design_id;
            const styleVariables = isImported
                ? undefined
                : themeToStyleVariables(optionBlock.theme);
            return (
                <CardTag
                    className={cn(
                        optionBlock.theme.tailwindClasses,
                        // `option` opts creator-built cards into the
                        // label.option image layout rules in app.css.
                        !isImported && "option",
                        isInDesignContext && "relative z-[1000]",
                    )}
                    style={styleVariables}
                    data-selected={option.id === watchedValue}
                    data-content-block-id={option.content_block_id}
                    data-parent-content-block-id={parentId}
                    data-option-card-id={option.content_block_id}
                >
                    {isInDesignContext && (
                        <CardOptionLayoutControls contentBlockId={contentBlockId} />
                    )}
                    {isImported ? (
                        children
                    ) : (
                        // Creator-built cards carry `p-0` and an image_url
                        // set in the editor; the card supplies the image and
                        // the text padding, as the default card render does.
                        <>
                            <CardOptionImage optionBlock={optionBlock} />
                            <span className="p-6">{children}</span>
                        </>
                    )}
                </CardTag>
            );
        }

        // Default card render — preserves the existing chrome for non-imported
        // card-variant questions in the rest of the app.
        const labelClasses = cn([defaultCardLabelClasses, "relative", "group/OPTION"]);
        const styleVariables = themeToStyleVariables(optionBlock?.theme || {});
        // <div> in the editor, <label> for participants — see the
        // tailwindClasses branch above for why the label breaks text editing.
        const CardTag = (isInDesignContext ? "div" : "label") as
            | "div"
            | "label";
        return (
            <CardTag
                className={cn([
                    "has-[:checked]:border-[var(--border-color-active)]",
                    labelClasses,
                    "hover:border-[var(--border-color-active)]",
                    "group/OPTION",
                    "option",
                    "overflow-visible",
                    "border-6",
                    "hover:scale-[1.02]",
                    "has-[:checked]:scale-[1.02]",
                    "transition-all",
                    "duration-300",
                    "ease-in-out",
                    isInDesignContext && "z-[1000]",
                ])}
                data-content-block-id={option.content_block_id}
                data-parent-content-block-id={parentId}
                data-option-card-id={option.content_block_id}
                style={
                    {
                        ...styleVariables,
                        "--border-color-active":
                            theme?.activeBorderColor || "#3ab765",
                        borderWidth: "6px",
                    } as React.CSSProperties
                }
            >
                {isInDesignContext && (
                    <CardOptionLayoutControls contentBlockId={contentBlockId} />
                )}
                <CardOptionImage optionBlock={optionBlock} />
                <span className="p-6">{children}</span>
            </CardTag>
        );
    }

    const styleColors: any = {};
    if (theme?.highLightStyle === "none") {
        styleColors["--border-color-active"] = theme?.highlightColor;
    }
    return (
        <FormLabel
            isSelected={option.id === watchedValue}
            palette={palette}
            highLightStyle={theme?.highLightStyle || "none"}
            highlightColor={theme?.highlightColor}
            className={`${getClasses(optionBlock)} default-options ${
                theme?.highLightStyle === "none"
                    ? "has-[:checked]:bg-[var(--border-color-active)]"
                    : ""
            }`}
            style={styleColors}
        >
            <div className="flex w-full flex-col items-start justify-center space-y-2">
                <span className="flex w-full flex-row items-center">
                    {children}
                </span>
            </div>
        </FormLabel>
    );
}

function getSliderLeftPct(min: number, max: number, position: number) {
    return ((position - min) / (max - min)) * 100;
}
type CustomError = {
    sum: { type: string };
    dependency: { type: string; error: any; valid: boolean };
    message: string;
};
const RenderFormField = ({
    promptId,
    prompt,
    palette,
    theme,
    optionRenderMap,
    isInDesignContext,
    promptBlock,
}: QuestionContentBlockFormFieldProps & {
    promptBlock?: ContentBlockShape;
}) => {
    const formContext = useFormContext();
    if(!formContext) return null;
    const { register, control, formState, setValue, trigger } = formContext;

    const { errors } = formState;
    const customErrors = errors as FieldErrors<{
        [index: string]: CustomError;
    }>;

    const error = useMemo(() => {
        const { errors } = formState;
        if (!!errors && !!errors[promptId] && "message" in errors[promptId]) {
            return errors[promptId] as FieldError;
        }
    }, [customErrors, promptId, formState]);

    const numericErrors = useMemo(() => {
        if (
            typeof customErrors[promptId] === "object" &&
            typeof customErrors[promptId]?.sum === "object" &&
            (customErrors[promptId]?.sum?.type === z.ZodIssueCode.too_big ||
                customErrors[promptId]?.sum?.type === z.ZodIssueCode.too_small)
        ) {
            return customErrors[promptId].sum;
        }
    }, [formState, promptId, customErrors]);

    const dependencyValidationErrors = useMemo(() => {
        if (
            !!errors &&
            !!errors[promptId] &&
            typeof customErrors[promptId] === "object" &&
            customErrors[promptId]?.dependency &&
            typeof customErrors[promptId]?.dependency === "object" &&
            customErrors[promptId]?.dependency?.error
        ) {
            return customErrors[promptId].dependency.error;
        }

        return {};
    }, [formState, promptId, customErrors]);

    const dependencyValidationValid = useMemo(() => {
        if (
            !!customErrors &&
            !!customErrors[promptId] &&
            typeof customErrors[promptId] === "object" &&
            customErrors[promptId]?.dependency &&
            typeof customErrors[promptId]?.dependency === "object" &&
            customErrors[promptId]?.dependency?.valid
        ) {
            return customErrors[promptId].dependency.valid;
        }

        return {};
    }, [customErrors, promptId]);

    // unselect options that are disabled due to dependency validation errors
    useEffect(() => {
        const keysToUncheck = Object.keys(dependencyValidationErrors);
        if (keysToUncheck.length) {
            const newValue = Array.isArray(watchedValue)
                ? watchedValue?.filter(
                      (value) => !keysToUncheck.includes(value),
                  )
                : "";

            setValue(promptId, newValue, {
                shouldTouch: true,
                shouldDirty: true,
            });
        }
    }, [dependencyValidationErrors, formState]);

    const registerReturn = register(promptId);

    const watchedValue = useWatch({
        control,
        name: promptId,
        defaultValue: prompt.default_numeric_value || 0,
    });

    switch (prompt.prompt_type) {
        case PromptType["Short Text"]:
            return (
                <>
                    <FormInput
                        type="text"
                        name={promptId}
                        {...registerReturn}
                    />
                    <QuestionContentBlockErrorDisplay error={error} />
                </>
            );
        case PromptType["Numerical Input"]:
            return (
                <>
                    <FormInput
                        type="number"
                        name={promptId}
                        {...registerReturn}
                        // min={
                        //     typeof prompt.min === "number"
                        //         ? prompt.min
                        //         : undefined
                        // }
                        // max={prompt.max || undefined}
                        // step={
                        //     prompt.increment !== undefined &&
                        //     prompt.increment !== 0
                        //         ? prompt.increment
                        //         : "any"
                        // }
                    />
                    <QuestionContentBlockErrorDisplay error={error} />
                </>
            );
        case PromptType["Dropdown List"]:
            return (
                <>
                    <FormSelect name={promptId} {...registerReturn}>
                        <FormOption key={""} value={""} disabled />
                        {!!prompt.options &&
                            prompt.options.map((option) => (
                                <FormOption
                                    key={option.id}
                                    value={option.content}
                                    data-option-id={option.id}
                                    data-content-block-id={
                                        option.content_block_id
                                    }
                                >
                                    {option.content}
                                </FormOption>
                            ))}
                    </FormSelect>
                    <QuestionContentBlockErrorDisplay error={error} />
                </>
            );
        case PromptType["Long Text"]:
            return (
                <>
                    <FormTextArea name={promptId} {...registerReturn} />
                    <QuestionContentBlockErrorDisplay error={error} />
                </>
            );
        case PromptType["Multiple Choice"]: {
            const isCards = promptBlock?.theme?.variant === "cards";
            return (
                <>
                    <CheckboxOrRadioGroup
                        promptBlock={promptBlock}
                        theme={theme}
                        palette={palette}
                        isInDesignContext={isInDesignContext}
                        prompt={prompt}
                        promptId={promptId}
                        optionRenderMap={optionRenderMap}
                    >
                        <>
                            {!!prompt.options &&
                                prompt.options.map((option) => (
                                    <OptionWithContentBlock
                                        key={option.id}
                                        contentBlockId={option.content_block_id}
                                    >
                                        {(optionBlock) => (
                                            <FormLabelWithContentBlock
                                                contentBlockId={
                                                    option.content_block_id
                                                }
                                                option={option}
                                                watchedValue={watchedValue}
                                                theme={theme}
                                                palette={palette}
                                                parentId={promptId}
                                                type="radio"
                                                promptId={promptId}
                                                isCards={isCards}
                                                isInDesignContext={isInDesignContext}
                                            >
                                                <>
                                                    <FormInputRadioCheckbox
                                                        data-option-id={
                                                            option.id
                                                        }
                                                        type="radio"
                                                        name={promptId}
                                                        value={option.id}
                                                        {...registerReturn}
                                                        disabled={
                                                            // In the editor the
                                                            // option's label wraps
                                                            // an editable Tiptap
                                                            // block; an enabled
                                                            // hidden radio would
                                                            // grab focus on label
                                                            // click and yank the
                                                            // caret out of the
                                                            // editor. Disable it in
                                                            // design context — the
                                                            // form is not interactive
                                                            // there anyway.
                                                            isInDesignContext ||
                                                            !!dependencyValidationErrors?.[
                                                                option.id
                                                            ]
                                                        }
                                                        defaultChecked={
                                                            option.id ===
                                                            watchedValue
                                                        }
                                                        checked={
                                                            watchedValue ===
                                                            option.id
                                                        }
                                                        checkMarkColor={
                                                            theme?.checkMarkColor
                                                        }
                                                        className="radio-hide data-[is-cards=true]:appearance-none data-[is-cards=true]:opacity-0"
                                                        data-is-cards={
                                                            isCards ||
                                                            optionBlock?.theme
                                                                ?.variant ===
                                                            "cards"
                                                        }
                                                    />
                                                    {(isCards ||
                                                        optionBlock?.theme
                                                            ?.variant ===
                                                        "cards") &&
                                                    !!optionBlock ? (
                                                        <RichTextBlock
                                                            contentBlock={
                                                                optionBlock
                                                            }
                                                            editable={
                                                                isInDesignContext
                                                            }
                                                            content={
                                                                optionBlock.rich_text
                                                            }
                                                        />
                                                    ) : (
                                                        <span>
                                                            {option.content}
                                                        </span>
                                                    )}

                                                    {(!!dependencyValidationErrors?.[
                                                        option.id
                                                    ] ||
                                                        !!dependencyValidationValid[
                                                            option.id
                                                        ]) && (
                                                        <div className="text-xs text-blue-600">
                                                            <InformationCircleIcon className="mr-1 inline-block h-6 w-6" />
                                                            {
                                                                dependencyValidationErrors?.[
                                                                    option.id
                                                                ]?.message
                                                            }
                                                            {
                                                                dependencyValidationValid?.[
                                                                    option.id
                                                                ]?.message
                                                            }
                                                        </div>
                                                    )}
                                                </>
                                            </FormLabelWithContentBlock>
                                        )}
                                    </OptionWithContentBlock>
                                ))}
                        </>
                    </CheckboxOrRadioGroup>
                    <QuestionContentBlockErrorDisplay error={error} />
                </>
            );
        }
        case PromptType["Multiple Select"]:
            return (
                <>
                    <FormRadioOrCheckboxGroup
                        spaced={theme?.spaced || "spaced"}
                        palette={palette}
                        bordered={theme?.bordered || "not bordered"}
                        isInDesignContext={isInDesignContext}
                        hasMaxWidth={!theme?.tailwindClasses?.length}
                    >
                        <>
                            {!!prompt.options &&
                                prompt.options.map((option) => (
                                    <FormLabel
                                        key={option.id}
                                        isSelected={
                                            option.id === watchedValue ||
                                            (Array.isArray(watchedValue) &&
                                                watchedValue.includes(
                                                    option.id,
                                                ))
                                        }
                                        palette={palette}
                                        highLightStyle={
                                            theme?.highLightStyle || "none"
                                        }
                                        highlightColor={theme?.highlightColor}
                                        data-content-block-id={
                                            option.content_block_id
                                        }
                                    >
                                        <div className="flex w-full flex-col items-start justify-center space-y-2">
                                            <span className="flex w-full flex-row items-center">
                                                <FormInputRadioCheckbox
                                                    type="checkbox"
                                                    name={promptId}
                                                    value={option.id}
                                                    {...registerReturn}
                                                    data-option-id={option.id}
                                                    disabled={
                                                        !!dependencyValidationErrors?.[
                                                            option.id
                                                        ]
                                                    }
                                                    checkMarkColor={
                                                        theme?.checkMarkColor
                                                    }
                                                />
                                                <span className="mt-1">
                                                    {option.content}
                                                </span>
                                            </span>
                                            {(!!dependencyValidationErrors?.[
                                                option.id
                                            ] ||
                                                !!dependencyValidationValid[
                                                    option.id
                                                ]) && (
                                                <div className="text-xs text-blue-600">
                                                    <InformationCircleIcon className="mr-1 inline-block h-6 w-6" />
                                                    {
                                                        dependencyValidationErrors?.[
                                                            option.id
                                                        ]?.message
                                                    }
                                                    {
                                                        dependencyValidationValid?.[
                                                            option.id
                                                        ]?.message
                                                    }
                                                </div>
                                            )}
                                        </div>
                                    </FormLabel>
                                ))}
                        </>
                    </FormRadioOrCheckboxGroup>
                    {numericErrors && (
                        <div className="mt-1">
                            <span
                                className={`text-lg font-normal tracking-wide ${
                                    numericErrors.type ===
                                    z.ZodIssueCode.too_big
                                        ? "text-blue-600"
                                        : "text-red-700"
                                }`}
                            >
                                {numericErrors.message}
                            </span>
                        </div>
                    )}
                    <QuestionContentBlockErrorDisplay error={error} />
                </>
            );
        case PromptType["Numerical Slider"]:
            return (
                <>
                    {theme?.sliderValueType === "tooltip" && (
                        <>
                            <div className="relative px-[7px]">
                                {/** pad the left and right by 7 pixels to account for width of slider thumb */}
                                <div className="relative h-4">
                                    <div
                                        className="absolute -bottom-[4px] left-0 z-40 mb-2 h-2 max-w-sm opacity-100
                                            -translate-x-1/2"
                                        style={{
                                            marginLeft: `${getSliderLeftPct(
                                                prompt.min || 0,
                                                prompt.max || 100,
                                                Number(
                                                    watchedValue ??
                                                        prompt.default_numeric_value ??
                                                        0,
                                                ),
                                            )}%`,
                                        }}
                                    >
                                        <div className="relative shadow-md">
                                            <div
                                                className="-mt-4 truncate rounded px-4 py-1 text-xs text-white"
                                                style={{
                                                    backgroundColor:
                                                        theme?.highlightColor ||
                                                        "#0075ff",
                                                }}
                                            >
                                                {watchedValue ??
                                                    prompt.default_numeric_value ??
                                                    0}
                                            </div>
                                            <svg
                                                className="top-100 absolute left-0 h-2 w-full"
                                                x="0px"
                                                y="0px"
                                                viewBox="0 0 255 255"
                                                xmlSpace="preserve"
                                                style={{
                                                    color:
                                                        theme?.highlightColor ||
                                                        "#0075ff",
                                                }}
                                            >
                                                <polygon
                                                    className="fill-current"
                                                    points="0,0 127.5,127.5 255,0"
                                                ></polygon>
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <FormInputRange
                                hasMargin={true}
                                className="m-0"
                                id={`slider-${promptId}`}
                                type="range"
                                name={promptId}
                                {...registerReturn}
                                min={prompt.min || 0}
                                max={prompt.max || 100}
                                step={!!prompt.increment ? prompt.increment : 1}
                                color={theme?.highlightColor || "#0075ff"}
                                defaultValue={
                                    Number(prompt.default_numeric_value) || 0
                                }
                            />
                            <div
                                className="mt-1"
                                data-message-location={promptId}
                            ></div>
                        </>
                    )}

                    {(theme?.sliderValueType === "label_on_left" ||
                        theme?.sliderValueType === "label_on_right") && (
                        <>
                            <div
                                className={`flex items-center gap-1 ${
                                    theme?.sliderValueType === "label_on_left"
                                        ? "flex-row-reverse"
                                        : "flex-row"
                                }`}
                            >
                                <FormInputRange
                                    id={`slider-${promptId}`}
                                    type="range"
                                    name={promptId}
                                    {...registerReturn}
                                    min={prompt.min || 0}
                                    max={prompt.max || 100}
                                    step={
                                        !!prompt.increment
                                            ? prompt.increment
                                            : 1
                                    }
                                />
                                <label
                                    htmlFor={`slider-${promptId}`}
                                    className="flex max-w-sm items-center p-2 text-lg font-bold"
                                >
                                    {watchedValue}
                                </label>
                            </div>
                        </>
                    )}
                    {(!theme?.sliderValueType ||
                        theme?.sliderValueType === "none") && (
                        <FormInputRange
                            type="range"
                            name={promptId}
                            {...registerReturn}
                            min={prompt.min || 0}
                            max={prompt.max || 100}
                            step={!!prompt.increment ? prompt.increment : 1}
                        />
                    )}
                    <QuestionContentBlockErrorDisplay error={error} />
                </>
            );
        case PromptType["Rank Order"]:
            return (
                <>
                    {((!!setValue && !!watchedValue?.length) ||
                        !!prompt.options) && (
                        <Controller
                            name={promptId || ""}
                            control={control}
                            rules={{ required: false }}
                            render={(props) => (
                                <QuestionContentBlockRankingComponent
                                    {...props}
                                    controlledFormFieldData={watchedValue}
                                    updateControlledFormFieldData={(
                                        newData: SelectionShape[],
                                    ) => setValue(promptId, newData)}
                                    options={prompt.options}
                                />
                            )}
                        />
                    )}
                    <QuestionContentBlockErrorDisplay error={error} />
                </>
            );
        case PromptType["Toggle Switch"]:
            return (
                <>
                    <div className="flex flex-row items-center">
                        <FormToggleLeftLabel>
                            {!!prompt.options &&
                                prompt.options.length > 0 &&
                                prompt.options[0].content}
                        </FormToggleLeftLabel>
                        <FormToggleDiv>
                            <FormInputToggle
                                type="checkbox"
                                id="toggle"
                                name={promptId}
                                {...registerReturn}
                                data-option-id={prompt.options[0].id}
                            />
                            <FormToggleLabel htmlFor="toggle" />
                        </FormToggleDiv>
                        <FormToggleRightLabel>
                            {!!prompt.options &&
                                prompt.options.length > 1 &&
                                prompt.options[1].content}
                        </FormToggleRightLabel>
                    </div>
                    <QuestionContentBlockErrorDisplay error={error} />
                </>
            );
        case PromptType["Drag and Drop"]:
            return (
                <>
                    {((!!setValue && !!watchedValue?.length) ||
                        !!prompt.options) && (
                        <Controller
                            name={promptId || ""}
                            control={control}
                            rules={{ required: false }}
                            render={(props) => {
                                return (
                                    <QuestionContentBlockDragDropComponent
                                        {...props}
                                        controlledFormFieldData={watchedValue}
                                        updateControlledFormFieldData={(
                                            newData: SelectionShape[],
                                        ) => {
                                            setValue(promptId, newData);
                                            trigger(promptId);
                                        }}
                                        prompt={prompt}
                                        optionRenderMap={optionRenderMap}
                                        isInDesignContext={isInDesignContext}
                                    />
                                );
                            }}
                        />
                    )}
                    {numericErrors && (
                        <div className="mt-1">
                            <span
                                className={`text-lg font-normal tracking-wide ${
                                    numericErrors.type ===
                                    z.ZodIssueCode.too_big
                                        ? "text-blue-600"
                                        : "text-red-700"
                                }`}
                            >
                                {numericErrors.message}
                            </span>
                        </div>
                    )}
                    <QuestionContentBlockErrorDisplay error={error} />
                </>
            );
        case PromptType.Timeline:
            return (
                <>
                    {((!!setValue && !!watchedValue?.length) ||
                        !!prompt.options) && (
                        <Controller
                            name={promptId || ""}
                            control={control}
                            rules={{ required: false }}
                            render={(props) => (
                                <QuestionContentBlockTimeline
                                    {...props}
                                    controlledFormFieldData={watchedValue}
                                    updateControlledFormFieldData={(
                                        newData: SelectionShape[],
                                    ) => setValue(promptId, newData)}
                                    prompt={prompt}
                                    optionRenderMap={optionRenderMap}
                                    isInDesignContext={isInDesignContext}
                                />
                            )}
                        />
                    )}
                    <QuestionContentBlockErrorDisplay error={error} />
                </>
            );
        default:
            return null;
    }
};

export const QuestionContentBlockFormField = (
    props: QuestionContentBlockFormFieldProps & { isSelected?: boolean },
) => {
    const { isSelected } = props;
    const promptBlock = useGetContentBlockWithChildren(props.contentBlockId);
    return isSelected ? (
        <>
            <RenderFormField {...props} promptBlock={promptBlock} />
        </>
    ) : (
        <RenderFormField {...props} promptBlock={promptBlock} />
    );
};

function OptionWithContentBlock({
    children,
    contentBlockId,
}: {
    contentBlockId: string;
    children: (contentBlock: ContentBlockShape) => React.ReactNode;
}) {
    const contentBlock = useGetContentBlockWithChildren(contentBlockId);

    const render = (contentBlock: ContentBlockShape) => {
        if (contentBlock) {
            return <>{children(contentBlock)}</>;
        }

        return children(null);
    };

    return render(contentBlock);
}
